import React from 'react';
import { Form, InputGroup, Button } from 'react-bootstrap';
import ThemedSelect from '../../store/ThemedSelect';

/**
 * Presentational controls that pair with useListControls. Each renders a
 * react-bootstrap <Form.Group> so it inherits the existing
 * `.filters-row .form-group` flex sizing when placed inside <FilterBar>.
 */

/**
 * Live search box (debouncing lives in the hook). Spread the hook's `search`:
 *   <SearchInput label="Search schools" {...search} />
 */
export const SearchInput = ({ label, placeholder, value, onChange, id }) => (
  <Form.Group controlId={id}>
    {label ? <Form.Label>{label}</Form.Label> : null}
    <InputGroup>
      <InputGroup.Text>
        <i className="bi bi-search" />
      </InputGroup.Text>
      <Form.Control
        type="text"
        placeholder={placeholder || 'Search...'}
        value={value}
        onChange={onChange}
      />
    </InputGroup>
  </Form.Group>
);

/**
 * A standardized filter dropdown with a leading "All" option. Spread one entry
 * of the hook's `filterList`:
 *   {filterList.map((f) => <FilterSelect key={f.filterKey} {...f} />)}
 *
 * Pass `searchable` for long option lists (e.g. hundreds of schools): renders a
 * type-ahead ThemedSelect instead of a native <select>. Clearing it (or picking
 * the "All" entry) reports the allOption value back to the hook.
 */
export const FilterSelect = ({ label, value, onChange, options = [], allOption, id, searchable }) => {
  const all = allOption ?? { value: 'all', label: 'All' };

  if (searchable) {
    const selectOptions = [all, ...options];
    const selected = selectOptions.find((opt) => opt.value === value) ?? all;
    return (
      // list-filter-select gives the control real width: react-bootstrap v2's
      // Form.Group renders a bare div (no .form-group class), and react-select
      // otherwise collapses to placeholder width inside the flex filters-row.
      <Form.Group controlId={id} className="list-filter-select">
        {label ? <Form.Label>{label}</Form.Label> : null}
        <ThemedSelect
          isSearchable
          isClearable={value !== all.value}
          options={selectOptions}
          value={selected}
          onChange={(opt) => onChange(opt ? opt.value : all.value)}
          inputId={id}
        />
      </Form.Group>
    );
  }

  return (
    <Form.Group controlId={id} className="list-filter-select">
      {label ? <Form.Label>{label}</Form.Label> : null}
      <Form.Control as="select" value={value} onChange={onChange}>
        <option value={all.value}>{all.label}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Form.Control>
    </Form.Group>
  );
};

/**
 * "Sort by" dropdown + asc/desc toggle. Spread the hook's `sort`:
 *   <SortControl {...sort} />
 * Renders nothing when there are no sort options (e.g. sortable:false).
 */
export const SortControl = ({
  activeKey,
  direction,
  options = [],
  onChangeKey,
  onToggleDirection,
}) => {
  if (!options.length) return null;
  return (
    <Form.Group controlId="listSort" className="list-sort-control">
      <Form.Label>Sort by</Form.Label>
      <div className="sort-inline">
        <Form.Control as="select" value={activeKey ?? ''} onChange={onChangeKey}>
          <option value="">None</option>
          {options.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {opt.label}
            </option>
          ))}
        </Form.Control>
        <Button
          variant="outline-secondary"
          className="sort-direction-toggle"
          onClick={onToggleDirection}
          disabled={!activeKey}
          title={direction === 'desc' ? 'Descending' : 'Ascending'}
          aria-label={direction === 'desc' ? 'Sort descending' : 'Sort ascending'}
        >
          <i className={`bi ${direction === 'desc' ? 'bi-sort-down' : 'bi-sort-up'}`} />
        </Button>
      </div>
    </Form.Group>
  );
};

/**
 * "X of Y" count + a Clear button. Passed to <FilterBar summary={...}>:
 *   <ResultSummary count={resultCount} total={totalCount} noun="school"
 *                  onClear={clear} isFiltered={isFiltered} />
 */
export const ResultSummary = ({
  count,
  total,
  noun = 'result',
  nounPlural,
  onClear,
  isFiltered,
  hint,
}) => {
  const plural = nounPlural || `${noun}s`;
  const totalLabel = total === 1 ? noun : plural;
  return (
    <div className="list-result-summary">
      <span className="result-count">
        {count === total ? `${total} ${totalLabel}` : `${count} of ${total} ${plural}`}
      </span>
      {hint ? <span className="result-hint">{hint}</span> : null}
      {onClear ? (
        <Button
          variant="link"
          className="result-clear"
          onClick={onClear}
          disabled={!isFiltered}
        >
          Clear
        </Button>
      ) : null}
    </div>
  );
};
